import { useEffect, useRef, useState } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from '~shared/shadcn/ui/pagination';

const PAGE_GAP = 'ellipsis' as const;

type PaginationEntry = number | typeof PAGE_GAP;

// Approximate width (px) of a single pagination control (button + gap), used to
// figure out how many page number buttons fit within the available container width.
const PAGE_BUTTON_WIDTH = 44;
// Fixed controls that are always rendered alongside the numbered pages:
// first, prev, next, last buttons.
const FIXED_CONTROL_COUNT = 4;

/**
 * Builds a truncated page list: first, last, current, and `siblingCount`
 * siblings on each side, collapsing any gaps with `'ellipsis'`.
 */
const getPaginationEntries = (
  currentPage: number,
  totalPages: number,
  siblingCount = 1,
): Array<PaginationEntry> => {
  const totalVisible = siblingCount * 2 + 5; // first, last, current, siblings on each side, 2 ellipses

  if (totalPages <= totalVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  const entries: Array<PaginationEntry> = [1];

  if (shouldShowLeftEllipsis) {
    entries.push(PAGE_GAP);
  } else {
    for (let page = 2; page < leftSiblingIndex; page++) {
      entries.push(page);
    }
  }

  for (let page = leftSiblingIndex; page <= rightSiblingIndex; page++) {
    if (page !== 1 && page !== totalPages) {
      entries.push(page);
    }
  }

  if (shouldShowRightEllipsis) {
    entries.push(PAGE_GAP);
  } else {
    for (let page = rightSiblingIndex + 1; page < totalPages; page++) {
      entries.push(page);
    }
  }

  entries.push(totalPages);

  return entries;
};

interface TruncatedPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Optional total item count, shown in the "Page X of Y (N <itemLabel>)" summary. */
  totalItems?: number;
  /** Label for the counted items, e.g. "students". */
  itemLabel?: string;
  className?: string;
}

/**
 * Page-number pagination that stays within its container's width by
 * collapsing distant page numbers behind an ellipsis, and automatically
 * widens/narrows the visible window as the container is resized.
 *
 * Built on top of the shared `~shared/shadcn/ui/pagination` primitives.
 */
export const TruncatedPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemLabel,
  className,
}: TruncatedPaginationProps) => {
  // Measured on the full-width wrapper, not the <ul>, since the <ul> only
  // ever sizes itself to its own content and would otherwise create a
  // feedback loop that keeps the pagination stuck at its smallest width.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [siblingCount, setSiblingCount] = useState(1);

  useEffect(() => {
    const container = wrapperRef.current;
    if (!container) return;

    const updateSiblingCount = () => {
      const availableWidth =
        container.clientWidth - FIXED_CONTROL_COUNT * PAGE_BUTTON_WIDTH;
      const maxNumberedSlots = Math.max(
        1,
        Math.floor(availableWidth / PAGE_BUTTON_WIDTH),
      );
      // Slots are split between: first page, last page, current page, and 2 * siblingCount.
      const nextSiblingCount = Math.max(
        1,
        Math.floor((maxNumberedSlots - 3) / 2),
      );
      setSiblingCount(nextSiblingCount);
    };

    updateSiblingCount();

    const resizeObserver = new ResizeObserver(updateSiblingCount);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [totalPages]);

  const goToPage = (page: number, disabled: boolean) => {
    if (disabled) return;
    onPageChange(page);
  };

  const linkClassName = (disabled: boolean) =>
    cn(disabled && 'pointer-events-none opacity-50');

  return (
    <div ref={wrapperRef} className={cn('w-full', className)}>
      <Pagination>
        <PaginationContent className='flex-wrap'>
          <PaginationItem>
            <PaginationLink
              href='#'
              size='sm'
              aria-label='Go to first page'
              aria-disabled={currentPage === 1}
              className={linkClassName(currentPage === 1)}
              onClick={(e) => {
                e.preventDefault();
                goToPage(1, currentPage === 1);
              }}
            >
              &laquo;
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href='#'
              size='sm'
              aria-label='Go to previous page'
              aria-disabled={currentPage === 1}
              className={linkClassName(currentPage === 1)}
              onClick={(e) => {
                e.preventDefault();
                goToPage(Math.max(1, currentPage - 1), currentPage === 1);
              }}
            >
              &lsaquo;
            </PaginationLink>
          </PaginationItem>

          {getPaginationEntries(currentPage, totalPages, siblingCount).map(
            (entry, index) =>
              entry === PAGE_GAP ? (
                <PaginationItem key={`gap-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={entry}>
                  <PaginationLink
                    href='#'
                    size='sm'
                    isActive={currentPage === entry}
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(entry, false);
                    }}
                    className={
                      currentPage === entry
                        ? 'bg-[#4F46E5] text-white hover:bg-[#4F46E5] hover:text-white'
                        : 'border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]'
                    }
                  >
                    {entry}
                  </PaginationLink>
                </PaginationItem>
              ),
          )}

          <PaginationItem>
            <PaginationLink
              href='#'
              size='sm'
              aria-label='Go to next page'
              aria-disabled={currentPage === totalPages}
              className={linkClassName(currentPage === totalPages)}
              onClick={(e) => {
                e.preventDefault();
                goToPage(
                  Math.min(totalPages, currentPage + 1),
                  currentPage === totalPages,
                );
              }}
            >
              &rsaquo;
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink
              href='#'
              size='sm'
              aria-label='Go to last page'
              aria-disabled={currentPage === totalPages}
              className={linkClassName(currentPage === totalPages)}
              onClick={(e) => {
                e.preventDefault();
                goToPage(totalPages, currentPage === totalPages);
              }}
            >
              &raquo;
            </PaginationLink>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      {totalItems != null && (
        <p className='mt-2 text-center text-sm text-[#6B7280]'>
          Page {currentPage} of {totalPages}
          {itemLabel ? ` (${totalItems} ${itemLabel})` : ` (${totalItems})`}
        </p>
      )}
    </div>
  );
};
